import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from '../Stateless/SnackBarProvider';
import { useBannerApi } from '../../hooks/useBannerApi';
import { useEffect } from 'react';
import { useQueryParams } from '../HelperFunctions/Hooks/useQueryParams';

export const UseCreateBanner = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { createBanner } = useBannerApi();
  return useMutation({
    mutationKey: ['CreateBanner'],
    mutationFn: (data: any) => createBanner(data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Banner created successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to create Banner',
        'error'
      );
    },
  });
};

export const usePatchBanner = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { getToggleStatus } = useBannerApi();

  return useMutation({
    mutationKey: ['PatchChoice'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      getToggleStatus({ id, status: data.status }),
    onSuccess: (_response, variables) => {
      const action =
        variables.data.status === 'unblocked' ? 'unblocked' : 'blocked';
      showMessage(`Banner ${action} successfully`, 'success');
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type || 'Failed to block the banner',
        'error'
      );
    },
  });
};

export const useDeleteBanner = () => {
  const queryClient = useQueryClient();
  const { deleteBanner } = useBannerApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteChoice'],
    mutationFn: (ids: string[]) => {
      return deleteBanner(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Banner deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to deleted Banner',
        'error'
      );
    },
  });
};

export const useEditBanner = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editBanner } = useBannerApi();

  return useMutation({
    mutationKey: ['EditBanner'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editBanner(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Banner Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update the Banner',
        'error'
      );
    },
  });
};

export const useAllBanner = ({
  isMainScreen = false,
  initialData,
  key,
}: {
  initialData?: any;
  isMainScreen?: boolean;
  key?: any;
}) => {
  const { query, queryhash, setFilters, filters, setQuery, updateQuery } =
    useQueryParams<any>({
      isMainScreen,
      initialData,
    });
  const { getAllBanner } = useBannerApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['settings', { hasParams: queryhash?.length === 0 }, { key }],
    queryFn: () => {
      return getAllBanner(query);
    },
  });

  useEffect(() => {
    if (queryhash) {
      refetch();
    }
  }, [queryhash]);

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    query,
    setQuery,
    filters,
    setFilters,
    updateQuery,
  };
};
